import React, { useEffect, useState } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Form from '@amzn/awsui-components-console/form';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import {
  SG_NAME_MAX_LENGTH,
  SG_NAME_MIN_LENGTH,
} from 'src/features/namespace/constants';
import { getNameError } from 'src/features/namespace/components/createStringGroupValidation';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface RenameStringGroupModalProps {
  visible: boolean;
  /** The group's current name (also its identifier while unsubmitted). */
  currentName: string;
  /**
   * Persists the rename. Called only with a valid name that differs from
   * {@link currentName}; an unchanged (but valid) name simply dismisses.
   */
  onSubmit: (newName: string) => void;
  onDismiss: () => void;
}

/**
 * Renames a not-yet-submitted string group before its first submission.
 *
 * A brand-new group's name IS its identifier and exists only in the browser
 * until the first submit, so it can be corrected freely here — sparing the user
 * from re-entering every string just to fix a typo in the name. The name field
 * reuses the Create String Group form's validation so the two can never
 * disagree on what a valid identifier is.
 */
const RenameStringGroupModal: React.FC<RenameStringGroupModalProps> = ({
  visible,
  currentName,
  onSubmit,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(currentName);
  const [showValidation, setShowValidation] = useState(false);

  // Re-seed the field from the current name each time the modal opens, so a
  // cancelled edit never lingers into the next open.
  useEffect(() => {
    if (visible) {
      setName(currentName);
      setShowValidation(false);
    }
  }, [visible, currentName]);

  const handleSave = () => {
    setShowValidation(true);
    if (getNameError(t, name, true) !== null) {
      return;
    }
    const trimmed = name.trim();
    // A valid but unchanged name is a no-op — just close, without churning the
    // URL / draft key through a rename to the identical value.
    if (trimmed === currentName) {
      onDismiss();
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={t('sgd-rename-title')}
      closeAriaLabel={t('sgd-rename-close')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              {t('string-cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave}>
              {t('sgd-rename-confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <FormField
          label={t('sgd-field-name')}
          constraintText={t('sgd-rename-name-constraint', {
            min: SG_NAME_MIN_LENGTH,
            max: SG_NAME_MAX_LENGTH,
          })}
          errorText={getNameError(t, name, showValidation)}
        >
          <Input
            value={name}
            placeholder={t('sgd-rename-placeholder')}
            onChange={({ detail }) => setName(detail.value)}
            ariaLabel={t('sgd-rename-name-aria')}
          />
        </FormField>
      </Form>
    </Modal>
  );
};

export default RenameStringGroupModal;
